import { useState } from 'react';
import '../tailwind.css';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/Reporting/ui/card';
import ReportForm from '../../../components/Reporting/report-form';
import InventoryDamageTable from '../../../components/Reporting/inventory-damage-table';
import { HeaderMenu } from '../../../components/Reporting/header-menu';
import instance from '../../../services/api';
import { toast } from 'react-toastify';
import { PackageX } from 'lucide-react';

const PAGE_SIZE = 20;

export default function InventoryDamagePage() {
  const [allItems, setAllItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [filterParams, setFilterParams] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [summary, setSummary] = useState(null);

  const buildBody = (data, page) => ({
    businessUuid: data.businessUuid,
    businessId: data.businessId,
    startDate: data.startDate,
    endDate: data.endDate,
    startTime: data.startTime,
    endTime: data.endTime,
    useLocalTime: data.useLocalTime || false,
    pageSize: PAGE_SIZE,
    page,
  });

  const handleFormSubmit = async (data) => {
    setIsLoading(true);
    setCurrentPage(0);
    setFilterParams(data);
    setAllItems([]);

    try {
      const response = await instance.post(
        '/reports/inventory-damage',
        buildBody(data, 0)
      );

      const newItems = response.data.items || [];
      const pagination = response.data.pagination || {};

      setAllItems(newItems);
      setHasMoreData(pagination.hasNextPage || false);
      setTotalItems(pagination.totalItems || newItems.length);
      setSummary(response.data.summary || null);
      setCurrentPage(0);

      toast.success('Inventory Damage Report Generated Successfully');

      setTimeout(() => {
        document.getElementById('inventory-damage-report')?.scrollIntoView({
          behavior: 'smooth',
        });
      }, 100);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to generate inventory damage report. Please try again.';
      toast.error(errorMessage);
      setAllItems([]);
      setHasMoreData(false);
      setTotalItems(0);
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!filterParams || isLoadingMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const response = await instance.post(
        '/reports/inventory-damage',
        buildBody(filterParams, nextPage)
      );

      const newItems = response.data.items || [];
      const pagination = response.data.pagination || {};

      setAllItems((prev) => [...prev, ...newItems]);
      setHasMoreData(pagination.hasNextPage || false);
      setCurrentPage(nextPage);

      toast.success(`Loaded ${newItems.length} more events`);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to load more events. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderMenu
        currentPage="inventory-damage"
        reportData={allItems.length > 0 ? { items: allItems } : null}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="no-print mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <PackageX className="text-primary mr-3 h-6 w-6" />
              Generate Inventory Damage Report
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportForm
              onSubmit={handleFormSubmit}
              isLoading={isLoading}
              setReportData={(data) => setAllItems(data || [])}
              showProviders={false}
              showSections={false}
            />
          </CardContent>
        </Card>

        {isLoading && allItems.length === 0 && (
          <Card className="mb-8">
            <CardContent className="pt-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                <span className="text-foreground">Generating report...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {allItems.length > 0 && (
          <div id="inventory-damage-report">
            <InventoryDamageTable
              items={allItems}
              isLoading={isLoading}
              isLoadingMore={isLoadingMore}
              hasMoreData={hasMoreData}
              onLoadMore={handleLoadMore}
              totalItems={totalItems}
              summary={summary}
            />
          </div>
        )}
      </div>
    </div>
  );
}
